/**
 * David AI Recruiter - Candidate Service API
 * Handles AI-powered conversational interviews with candidates
 * Base URL: Configurable via VITE_DAVID_CANDIDATE_URL (default: http://localhost:8004)
 */

import axios, { AxiosError } from 'axios';
import { handleApiError, ServiceError } from '../../shared/utils/errorHandler';
import { ENV } from '../../config/env';

const DAVID_CANDIDATE_BASE_URL = ENV.DAVID_CANDIDATE_URL;

/**
 * Handle FastAPI error responses
 * FastAPI returns errors in format: { detail: "error message" }
 */
function handleDavidApiError(error: any): ServiceError {
    if (error instanceof AxiosError && error.response?.data?.detail) {
        const detail = error.response.data.detail;

        // Map common error messages to error codes
        let errorCode = 'UNKNOWN_ERROR';

        if (typeof detail === 'string') {
            if (detail.includes('already been completed')) {
                errorCode = 'CONVERSATION_ALREADY_COMPLETED';
            } else if (detail.includes('Session not found')) {
                errorCode = 'CONVERSATION_NOT_FOUND';
            } else if (detail.includes('Candidate not found')) {
                errorCode = 'CANDIDATE_NOT_FOUND';
            } else if (detail.includes('Message cannot be empty')) {
                errorCode = 'EMPTY_MESSAGE';
            } else if (detail.includes('LLM') || detail.includes('OpenRouter')) {
                errorCode = 'LLM_ERROR';
            } else if (detail.includes('Database')) {
                errorCode = 'DATABASE_ERROR';
            }
        }

        return new ServiceError(
            typeof detail === 'string' ? detail : JSON.stringify(detail),
            'david-candidate',
            errorCode,
            error.response.status
        );
    }

    return handleApiError(error, 'david-candidate');
}

// ============================================================================
// Enums
// ============================================================================

export enum ConversationStage {
    INTRODUCTION = 'introduction',
    BACKGROUND_1 = 'background_1',
    BACKGROUND_2 = 'background_2',
    TRANSITION_TO_CAREER = 'transition_to_career',
    CAREER_1 = 'career_1',
    CAREER_2 = 'career_2',
    TRANSITION_TO_FUN = 'transition_to_fun',
    PERSONALITY = 'personality',
    COMPLETE = 'complete',
}

export enum QuestionType {
    BACKGROUND = 'background',
    CAREER_ASPIRATION = 'career_aspiration',
    PERSONALITY = 'personality',
}

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface Message {
    role: 'assistant' | 'user';
    content: string;
    timestamp: string;
}

export interface QuestionAnswer {
    question_type: QuestionType;
    question: string;
    answer: string | null;
    asked_at: string;
    answered_at: string | null;
}

export interface ChatRequest {
    session_id: string;
    candidate_id: string;
    message: string | null;
}

export interface ChatResponse {
    session_id: string;
    message: string;
    stage: ConversationStage;
    completed: boolean;
}

export interface ChatResponseV2 {
    session_id: string;
    message: string;
    mode: 'onboarding' | 'assistant';
    stage?: string;
    completed: boolean;
    tool_used?: string;
    structured_data?: Record<string, any>;
}

export interface SessionDetails {
    session_id: string;
    candidate_id: string;
    stage: ConversationStage;
    completed: boolean;
    created_at: string;
    updated_at: string;
    messages: Message[];
    questions: QuestionAnswer[];
}

export interface ConversationSummary {
    session_id: string;
    stage: ConversationStage;
    completed: boolean;
    created_at: string;
    updated_at: string;
    message_count: number;
}

export interface ConversationsListResponse {
    candidate_id: string;
    total_conversations: number;
    conversations: ConversationSummary[];
}

export interface CandidateProfileData {
    _id: string;
    name: string;
    email: string;
    phone?: string;
    skills?: string[];
    experience?: {
        work?: Array<{
            company_name: string;
            start_date: string;
            end_date: string;
            role_name: string;
            location_city?: string;
            location_state?: string;
            location_country?: string;
            responsibilities?: string[];
            skills?: string[];
        }>;
    };
    resume_data?: {
        education?: {
            education?: Array<{
                school_name: string;
                start_date: string;
                end_date: string;
                degree_name: string;
                grade?: string;
            }>;
        };
        certifications?: {
            certificates?: Array<{
                name: string;
                issuer: string;
            }>;
        };
        projects?: Array<{
            name: string;
            contribution?: string[];
            tech_skills?: string[];
            soft_skills?: string[];
        }>;
    };
}

export interface HealthCheckResponse {
    status: 'healthy' | 'unhealthy';
    service?: string;
    version?: string;
    port?: number;
    database?: string;
    timestamp?: string;
    error?: string;
}

// ============================================================================
// API Service Class
// ============================================================================

class DavidCandidateApi {
    // ========================================================================
    // Health Check APIs
    // ========================================================================

    /**
     * Root health check endpoint
     */
    async healthCheck(): Promise<HealthCheckResponse> {
        try {
            const response = await axios.get<HealthCheckResponse>(
                `${DAVID_CANDIDATE_BASE_URL}/`
            );
            return response.data;
        } catch (error: any) {
            throw handleDavidApiError(error);
        }
    }

    /**
     * Detailed health check with database status
     */
    async healthCheckDetailed(): Promise<HealthCheckResponse> {
        try {
            const response = await axios.get<HealthCheckResponse>(
                `${DAVID_CANDIDATE_BASE_URL}/health`
            );
            return response.data;
        } catch (error: any) {
            throw handleDavidApiError(error);
        }
    }

    /**
     * Versioned health check endpoint
     */
    async healthCheckVersioned(): Promise<HealthCheckResponse> {
        try {
            const response = await axios.get<HealthCheckResponse>(
                `${DAVID_CANDIDATE_BASE_URL}/david/v1/health`
            );
            return response.data;
        } catch (error: any) {
            throw handleDavidApiError(error);
        }
    }

    /**
     * Database connectivity health check
     */
    async healthCheckDatabase(): Promise<HealthCheckResponse> {
        try {
            const response = await axios.get<HealthCheckResponse>(
                `${DAVID_CANDIDATE_BASE_URL}/david/v1/health/database`
            );
            return response.data;
        } catch (error: any) {
            throw handleDavidApiError(error);
        }
    }

    // ========================================================================
    // Auth Helper
    // ========================================================================

    private getAuthHeaders(): Record<string, string> {
        const token = localStorage.getItem('authToken');
        if (!token) {
            throw new ServiceError('Not authenticated', 'david-candidate', 'AUTH_REQUIRED', 401);
        }
        return { Authorization: `Bearer ${token}` };
    }

    // ========================================================================
    // V2 Chat APIs (LangGraph — JWT required)
    // ========================================================================

    async startChatV2(sessionId: string, mode: 'onboarding' | 'assistant' = 'assistant'): Promise<ChatResponseV2> {
        try {
            const response = await axios.post<ChatResponseV2>(
                `${DAVID_CANDIDATE_BASE_URL}/candidate-ai/v2/chat/start`,
                { session_id: sessionId, mode },
                { headers: this.getAuthHeaders() }
            );
            return response.data;
        } catch (error: any) {
            throw handleDavidApiError(error);
        }
    }

    async sendMessageV2(sessionId: string, message: string): Promise<ChatResponseV2> {
        try {
            if (!message || message.trim().length === 0) {
                throw new ServiceError('Message cannot be empty', 'david-candidate', 'EMPTY_MESSAGE', 400);
            }
            const response = await axios.post<ChatResponseV2>(
                `${DAVID_CANDIDATE_BASE_URL}/candidate-ai/v2/chat/message`,
                { session_id: sessionId, message },
                { headers: this.getAuthHeaders() }
            );
            return response.data;
        } catch (error: any) {
            throw handleDavidApiError(error);
        }
    }

    async getSessionV2(sessionId: string): Promise<any> {
        try {
            const response = await axios.get(
                `${DAVID_CANDIDATE_BASE_URL}/candidate-ai/v2/chat/session/${sessionId}`,
                { headers: this.getAuthHeaders() }
            );
            return response.data;
        } catch (error: any) {
            throw handleDavidApiError(error);
        }
    }

    // ========================================================================
    // V1 Chat/Conversation APIs (Legacy)
    // ========================================================================

    /**
     * Start a new chat session with David AI Recruiter
     * @param candidateId - Candidate's MongoDB ObjectID
     * @param sessionId - Unique session identifier (UUID recommended)
     * @returns ChatResponse with David's introduction message
     */
    async startChat(candidateId: string, sessionId?: string): Promise<ChatResponse> {
        try {
            // Generate a session ID if not provided
            const generatedSessionId = sessionId || `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

            const requestData: ChatRequest = {
                session_id: generatedSessionId,
                candidate_id: candidateId,
                message: null,
            };

            const response = await axios.post<ChatResponse>(
                `${DAVID_CANDIDATE_BASE_URL}/candidate-ai/v1/chat/start`,
                requestData
            );

            return response.data;
        } catch (error: any) {
            throw handleDavidApiError(error);
        }
    }

    /**
     * Send a message in an ongoing chat session
     * @param sessionId - Session identifier from start response
     * @param candidateId - Candidate's MongoDB ObjectID
     * @param message - User's message (cannot be empty)
     * @returns ChatResponse with David's response
     */
    async sendMessage(
        sessionId: string,
        candidateId: string,
        message: string
    ): Promise<ChatResponse> {
        try {
            if (!message || message.trim().length === 0) {
                throw new ServiceError(
                    'Message cannot be empty',
                    'david-candidate',
                    'EMPTY_MESSAGE',
                    400
                );
            }

            const requestData: ChatRequest = {
                session_id: sessionId,
                candidate_id: candidateId,
                message: message,
            };

            const response = await axios.post<ChatResponse>(
                `${DAVID_CANDIDATE_BASE_URL}/candidate-ai/v1/chat/message`,
                requestData
            );

            return response.data;
        } catch (error: any) {
            throw handleDavidApiError(error);
        }
    }

    /**
     * Get complete conversation session details
     * @param sessionId - Session identifier
     * @returns SessionDetails with full conversation history
     */
    async getSession(sessionId: string): Promise<SessionDetails> {
        try {
            const response = await axios.get<SessionDetails>(
                `${DAVID_CANDIDATE_BASE_URL}/candidate-ai/v1/chat/session/${sessionId}`
            );

            return response.data;
        } catch (error: any) {
            throw handleDavidApiError(error);
        }
    }

    /**
     * Get all conversation sessions for a candidate
     * @param candidateId - Candidate's MongoDB ObjectID
     * @returns ConversationsListResponse with list of all conversations
     */
    async getConversations(candidateId: string): Promise<ConversationsListResponse> {
        try {
            const response = await axios.get<ConversationsListResponse>(
                `${DAVID_CANDIDATE_BASE_URL}/candidate-ai/v1/${candidateId}/conversations`
            );

            return response.data;
        } catch (error: any) {
            throw handleDavidApiError(error);
        }
    }

    // ========================================================================
    // Candidate Profile APIs
    // ========================================================================

    /**
     * Get candidate profile information
     * @param candidateId - Candidate's MongoDB ObjectID
     * @returns CandidateProfileData with complete candidate profile
     */
    async getCandidateProfile(candidateId: string): Promise<CandidateProfileData> {
        try {
            const response = await axios.get<CandidateProfileData>(
                `${DAVID_CANDIDATE_BASE_URL}/candidate-ai/v1/${candidateId}`
            );

            return response.data;
        } catch (error: any) {
            throw handleDavidApiError(error);
        }
    }

    // ========================================================================
    // Utility Methods
    // ========================================================================

    /**
     * Generate a unique session ID
     * @returns A unique session identifier
     */
    generateSessionId(): string {
        return `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Check if a conversation is complete
     * @param stage - Current conversation stage
     * @returns boolean indicating if conversation is complete
     */
    isConversationComplete(stage: ConversationStage): boolean {
        return stage === ConversationStage.COMPLETE;
    }

    /**
     * Get human-readable stage name
     * @param stage - Conversation stage
     * @returns Human-readable stage name
     */
    getStageName(stage: ConversationStage): string {
        const stageNames: Record<ConversationStage, string> = {
            [ConversationStage.INTRODUCTION]: 'Introduction',
            [ConversationStage.BACKGROUND_1]: 'Background Discussion',
            [ConversationStage.BACKGROUND_2]: 'Background Deep Dive',
            [ConversationStage.TRANSITION_TO_CAREER]: 'Transitioning to Career Goals',
            [ConversationStage.CAREER_1]: 'Career Aspirations',
            [ConversationStage.CAREER_2]: 'Career Preferences',
            [ConversationStage.TRANSITION_TO_FUN]: 'Transitioning to Personality',
            [ConversationStage.PERSONALITY]: 'Personality Question',
            [ConversationStage.COMPLETE]: 'Complete',
        };

        return stageNames[stage] || 'Unknown Stage';
    }

    /**
     * Get question type from stage
     * @param stage - Conversation stage
     * @returns QuestionType or null
     */
    getQuestionTypeFromStage(stage: ConversationStage): QuestionType | null {
        if (stage === ConversationStage.BACKGROUND_1 || stage === ConversationStage.BACKGROUND_2) {
            return QuestionType.BACKGROUND;
        }
        if (stage === ConversationStage.CAREER_1 || stage === ConversationStage.CAREER_2) {
            return QuestionType.CAREER_ASPIRATION;
        }
        if (stage === ConversationStage.PERSONALITY) {
            return QuestionType.PERSONALITY;
        }
        return null;
    }
}

export const davidCandidateApi = new DavidCandidateApi();
